import { ChangeDetectionStrategy, Component, OnInit, QueryList, ViewChildren, forwardRef } from "@angular/core";
import { MobaroTrigger, SelectQuestion, SelectQuestionOption } from "../../../checklist.interface";
import { QuestionComponent } from "../question.component";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { IonCheckbox } from "@ionic/angular";

@Component({
	selector: "app-select-question",
	templateUrl: "./select-question.component.html",
	styleUrls: ["./select-question.component.scss"],
	changeDetection: ChangeDetectionStrategy.Default,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => SelectQuestionComponent),
			multi: true,
		},
	],
})
export class SelectQuestionComponent extends QuestionComponent<SelectQuestion> implements OnInit, ControlValueAccessor {
	@ViewChildren("checkboxRef") checkboxRefs: QueryList<IonCheckbox>;

	protected isOptionSelected = (option: string): boolean => {
		return this.answer?.answers == null ? false : this.answer?.answers?.includes(option);
	};

	protected options: { selectQuestionOption: SelectQuestionOption; showTrigger: boolean }[] = [];

	protected isSingleLineMode: boolean;
	protected select = async (option: string): Promise<void> => {
		const currentAnswers = this.answer?.answers || [];
		const isOptionAlreadySelected = this.isOptionSelected(option);

		this.onTouched();

		if (this.question.variation === "Multiple") {
			const answers = isOptionAlreadySelected
				? currentAnswers.filter((answer) => answer !== option)
				: currentAnswers.concat([option]);

			await this.setAnswers(...answers);
		} else {
			const answer = isOptionAlreadySelected ? undefined : option;
			await this.setAnswers(answer);
		}

		if (this.isSingleLineMode && !this.isV2()) {
			this.fixCheckboxUIOnAnswerReset(option);
		}
	};

	private fixCheckboxUIOnAnswerReset = (option: string): void => {
		// This is a workaround for resetting checkbox UI after click, if assignment is abandoned #815
		const checkbox = this.checkboxRefs.find((ref) => ref.value === option);
		if (checkbox) {
			checkbox.checked = this.isOptionSelected(option);
		}
	};

	private showAssignmentTrigger = (option: SelectQuestionOption, triggers: MobaroTrigger[]): boolean => {
		return triggers?.some((trigger) => {
			if (trigger.type !== "AssignmentTrigger" || trigger.requireNoIntervention) {
				return false;
			}

			const triggersOnEquals = trigger.condition.comparer == "EQ";
			const triggersOnNotEqual = trigger.condition.comparer === "NEQ";
			const refersToThisOption = trigger.condition.right === option.id;

			return (triggersOnEquals && refersToThisOption) || (triggersOnNotEqual && refersToThisOption === false);
		});
	};

	override ngOnInit() {
		super.ngOnInit();
		this.isSingleLineMode = this.question.options.some((option) => option.translatedText.length > 24);

		this.options = this.question.options.map((option) => {
			return {
				selectQuestionOption: option,
				showTrigger: this.showAssignmentTrigger(option, this.question.triggers),
			};
		});
	}
}
